// src/components/AddCita.jsx
import { useState, useEffect } from 'react';
import axiosInstance from '../axiosInstance';
import { toast } from 'react-toastify';
import {
  Box, TextField, Button, Select, MenuItem,
  FormControl, InputLabel, Paper, Typography,
  Divider, useTheme, CircularProgress,
  InputAdornment, useMediaQuery
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useNavigate } from 'react-router-dom';
import EventIcon from '@mui/icons-material/Event';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import SubjectIcon from '@mui/icons-material/Subject';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TimerIcon from '@mui/icons-material/Timer';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// NUEVOS ICONOS para Modalidad
import VideocamIcon from '@mui/icons-material/Videocam';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const primaryBlue = {
  main: '#0056b3',
  dark: '#004494',
  light: '#3378c1',
  contrastText: '#ffffff'
};

const AddCita = ({ onListChange, onClose, pacienteSeleccionado }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Altura y radios de inputs responsivos
  const inputHeight = isMobile ? 48 : 56;
  const inputRadius = isMobile ? '12px' : '14px';

  const [pacientes, setPacientes] = useState([]);
  const [pacienteId, setPacienteId] = useState('');
  const [medicoId, setMedicoId] = useState('');
  const [medicoNombre, setMedicoNombre] = useState('');
  const [fechaHora, setFechaHora] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [duracionConsulta, setDuracionConsulta] = useState(30);
  const [loading, setLoading] = useState(true);

  // NUEVO: estado de modalidad
  const [modalidad, setModalidad] = useState('Presencial'); // 'Presencial' | 'Telemedicina'

  const navigate = useNavigate();

  // Helper para manejar 429 con mensaje claro
  const getThrottleMessage = (err, fallback = 'Has alcanzado el límite de solicitudes. Intenta nuevamente en unos segundos.') => {
    const hdr = err?.response?.headers?.['retry-after'];
    if (hdr) return `Has alcanzado el límite de solicitudes. Intenta nuevamente en ~${parseInt(hdr, 10)}s.`;
    const detail = err?.response?.data?.detail || '';
    const m = String(detail).match(/(\d+)\s*seg/);
    if (m?.[1]) return `Has alcanzado el límite de solicitudes. Intenta nuevamente en ~${m[1]}s.`;
    return fallback;
  };

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchData = async () => {
      setLoading(true);
      try {
        // Cargar pacientes y médico en paralelo
        const [resPacientes, resMedico] = await Promise.all([
          axiosInstance.get('/pacientes/', { signal }),
          axiosInstance.get('/current-medico/', { signal }),
        ]);

        const lista = Array.isArray(resPacientes.data)
          ? resPacientes.data
          : (resPacientes.data?.results ?? []);
        setPacientes(lista);

        const medico = resMedico.data;
        const sexo = medico?.usuario?.sexo || 'M';
        const esF = String(sexo).toLowerCase().startsWith('f');
        const prefijo = esF ? 'Dra.' : 'Dr.';
        setMedicoId(medico.id_medico);
        const especialidad = medico.especialidad_principal ? ` - ${medico.especialidad_principal}` : '';
        setMedicoNombre(`${prefijo} ${medico?.usuario?.first_name || ''} ${medico?.usuario?.last_name || ''}${especialidad}`);
      } catch (err) {
        if (err?.name === 'CanceledError' || err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return;
        if (err?.response?.status === 429) {
          toast.warning(getThrottleMessage(err));
        } else {
          toast.error('Error al cargar pacientes o médico.');
        }
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (pacienteSeleccionado) {
      setPacienteId(pacienteSeleccionado);
    }
  }, [pacienteSeleccionado]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!pacienteId || pacienteId === 'nuevo_paciente') {
      toast.error('Selecciona un paciente válido.');
      return;
    }
    if (!fechaHora) {
      toast.error('Selecciona una fecha y hora válidas.');
      return;
    }

    setLoading(true);
    const nuevaCita = {
      paciente: pacienteId,
      medico: medicoId,
      fecha_hora_cita: fechaHora.toISOString(),
      motivo_cita: (motivo || '').trim() !== '' ? motivo.trim() : 'Sin motivo especificado',
      duracion_minutos: duracionConsulta,
      modalidad, // NUEVO: enviar modalidad al backend
    };

    try {
      await axiosInstance.post('/citas/', nuevaCita);
      toast.success('¡Cita creada con éxito!');
      onListChange?.();
      onClose?.();

      // Reset de campos
      setPacienteId('');
      setFechaHora(null);
      setMotivo('');
      setDuracionConsulta(30);
      setModalidad('Presencial'); // reset modalidad
    } catch (error) {
      console.error('Error al crear la cita:', error?.response?.data || error);
      if (error?.response?.status === 429) {
        toast.error(getThrottleMessage(error, 'Estás creando citas muy rápido. Intenta nuevamente en unos segundos.'));
      } else if (error?.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Hubo un error al crear la cita.');
      }
    } finally {
      setLoading(false);
    }
  };

  const pacienteMenuItems = loading
    ? [
        <MenuItem key="loading" disabled>
          <CircularProgress size={20} sx={{ mr: 1 }} />
          Cargando pacientes...
        </MenuItem>
      ]
    : [
        ...(pacientes || []).map((p) => (
          <MenuItem key={p.id_paciente} value={p.id_paciente}>
            {p.nombres} {p.apellidos}
          </MenuItem>
        )),
        <Divider key="divider" sx={{ my: 1 }} />,
        <MenuItem
          key="nuevo_paciente"
          value="nuevo_paciente"
          sx={{ color: primaryBlue.main, fontWeight: 500 }}
        >
          <AddIcon sx={{ mr: 1, fontSize: '1rem' }} /> Crear nuevo paciente
        </MenuItem>
      ];

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: isMobile ? 'flex-start' : 'center', width: '100%', height: '100%' }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 4, md: 6 },
          borderRadius: { xs: 0, sm: '16px', md: '20px' },
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          backgroundColor: theme.palette.background.paper,
          width: '100%',
          maxWidth: { xs: '100%', sm: '720px', md: '680px' }, // más ancho en sm para respirar
          boxShadow: { xs: 'none', sm: '0 8px 32px rgba(0, 0, 0, 0.08)' },
          position: 'relative',
          overflow: 'hidden',
          maxHeight: '100%',
          ...(isMobile && {
            // que el contenido pueda scrollear en móviles si el teclado ocupa espacio
            overflowY: 'auto'
          }),
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '6px',
            background: `linear-gradient(90deg, ${primaryBlue.main}, ${primaryBlue.light})`,
            borderTopLeftRadius: { xs: 0, sm: '16px', md: '20px' },
            borderTopRightRadius: { xs: 0, sm: '16px', md: '20px' },
          }
        }}
      >
        {/* Título (opcional: puedes agregar un título) */}
        <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarMonthIcon color="primary" />
          <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="bold">
            Programar Cita
          </Typography>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 2, sm: 3 },
            pb: { xs: 8, sm: 0 } // espacio extra para no esconderse bajo la barra sticky en móvil
          }}
        >
          {/* Select Paciente */}
          <FormControl fullWidth required>
            <InputLabel>Paciente</InputLabel>
            <Select
              value={pacienteId}
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'nuevo_paciente') {
                  onClose?.();
                  // Navega al listado correcto del área de médicos
                  navigate('/doctor/pacientes', { state: { fromAddCita: true, returnToCitas: true } });
                } else {
                  setPacienteId(value);
                }
              }}
              disabled={loading}
              sx={{ borderRadius: inputRadius, height: `${inputHeight}px` }}
              startAdornment={
                <InputAdornment position="start">
                  <PersonIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              }
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: '14px', // corregido (antes '14x')
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    maxHeight: isMobile ? 240 : 320
                  }
                }
              }}
            >
              {pacienteMenuItems}
            </Select>
          </FormControl>

          {/* Médico (solo display) */}
          <TextField
            label="Médico"
            value={medicoNombre}
            disabled
            fullWidth
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocalHospitalIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: inputRadius, height: `${inputHeight}px` } }}
          />

          {/* NUEVO: Modalidad */}
          <FormControl fullWidth required>
            <InputLabel>Modalidad</InputLabel>
            <Select
              value={modalidad}
              label="Modalidad"
              onChange={(e) => setModalidad(e.target.value)}
              sx={{ borderRadius: inputRadius, height: `${inputHeight}px` }}
              startAdornment={
                <InputAdornment position="start">
                  {modalidad === 'Telemedicina' ? (
                    <VideocamIcon sx={{ color: 'text.secondary' }} />
                  ) : (
                    <LocationOnIcon sx={{ color: 'text.secondary' }} />
                  )}
                </InputAdornment>
              }
              MenuProps={{
                PaperProps: {
                  sx: { borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }
                }
              }}
            >
              <MenuItem value="Presencial">
                <LocationOnIcon fontSize="small" style={{ marginRight: 8 }} />
                Presencial
              </MenuItem>
              <MenuItem value="Telemedicina">
                <VideocamIcon fontSize="small" style={{ marginRight: 8 }} />
                Telemedicina
              </MenuItem>
            </Select>

            {modalidad === 'Telemedicina' && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, color: 'text.secondary' }}>
                <InfoOutlinedIcon sx={{ fontSize: 18, mr: 1 }} />
                <Typography variant="caption">
                  Esta cita es virtual. El enlace se enviará antes de la consulta o se generará al iniciar la sesión.
                </Typography>
              </Box>
            )}
          </FormControl>

          {/* Fecha y Hora */}
          <DateTimePicker
            label="Fecha y Hora de la Cita"
            value={fechaHora}
            onChange={setFechaHora}
            slotProps={{
              textField: {
                required: true,
                fullWidth: true,
                InputProps: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccessTimeIcon sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                },
                sx: { '& .MuiOutlinedInput-root': { borderRadius: inputRadius, height: `${inputHeight}px` } }
              },
            }}
          />

          {/* Duración */}
          <FormControl fullWidth>
            <InputLabel>Duración de la consulta</InputLabel>
            <Select
              value={duracionConsulta}
              onChange={(e) => setDuracionConsulta(e.target.value)}
              label="Duración de la consulta"
              sx={{ borderRadius: inputRadius, height: `${inputHeight}px` }}
              startAdornment={
                <InputAdornment position="start">
                  <TimerIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              }
              MenuProps={{ PaperProps: { sx: { borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' } } }}
            >
              <MenuItem value={15}>15 minutos</MenuItem>
              <MenuItem value={30}>30 minutos</MenuItem>
              <MenuItem value={45}>45 minutos</MenuItem>
              <MenuItem value={60}>1 hora</MenuItem>
              <MenuItem value={90}>1 hora 30 minutos</MenuItem>
              <MenuItem value={120}>2 horas</MenuItem>
            </Select>
          </FormControl>

          {/* Motivo */}
          <TextField
            label="Motivo de la Cita (Opcional)"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            fullWidth
            multiline
            rows={isMobile ? 2 : 3}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                  <SubjectIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', pl: 1 } }}
          />

          {/* Barra de acciones (sticky en móvil) */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: { xs: 1, sm: 0 },
              mt: { xs: 0, sm: 2 },
              pt: { xs: 1, sm: 4 },
              borderTop: { xs: 'none', sm: `1px solid ${alpha(theme.palette.divider, 0.1)}` },
              position: { xs: 'sticky', sm: 'static' },
              bottom: 0,
              left: 0,
              right: 0,
              background: { xs: `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${theme.palette.background.paper} 100%)`, sm: 'transparent' },
              backdropFilter: { xs: 'saturate(180%) blur(6px)', sm: 'none' },
              zIndex: 2,
              py: { xs: 1, sm: 0 },
              flexDirection: { xs: 'column-reverse', sm: 'row' }
            }}
          >
            <Button
              onClick={onClose}
              variant="outlined"
              color="inherit"
              startIcon={<CancelIcon />}
              fullWidth={isMobile}
              sx={{
                borderRadius: '14px',
                px: 3,
                py: 1.2,
                borderColor: alpha(theme.palette.divider, 0.5),
                color: theme.palette.text.secondary,
                '&:hover': {
                  borderColor: theme.palette.divider,
                  backgroundColor: alpha(theme.palette.divider, 0.1)
                }
              }}
            >
              Cancelar
            </Button>

            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
              fullWidth={isMobile}
              sx={{
                borderRadius: '12px',
                px: 3,
                py: 1.2,
                backgroundColor: primaryBlue.main,
                boxShadow: '0 4px 12px rgba(0, 86, 179, 0.2)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: primaryBlue.dark,
                  boxShadow: '0 6px 16px rgba(0, 86, 179, 0.3)',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              {loading ? 'Programando...' : 'Programar Cita'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default AddCita;
